import { faChevronDown, faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useRef } from "react";
import { Button, Heading } from "react-aria-components";

import { useElementOverflowing } from "../../hooks/useElementOverflowing";
import { cn } from "../../utils/cn";
import { Tooltip } from "../tooltip/Tooltip";
import { useAccordionContext } from "./context";

import type { ButtonProps as RACButtonProps } from "react-aria-components";

export interface AccordionHeaderProps extends Omit<RACButtonProps, "className" | "children"> {
  /** Header text (allow string only instead of a React element) */
  children: string;
  /** Optional tooltip text. If provided, shows info icon with tooltip. */
  tooltipText?: string;
  /** Optional accessory element (e.g., badge, icon) to display after the title */
  headerAccessory?: React.ReactNode;
  showChevron?: boolean;
  level?: 1 | 2 | 3 | 4 | 5 | 6; // heading level
  className?: string;
}

export const AccordionHeader = forwardRef<HTMLButtonElement, AccordionHeaderProps>(
  (
    { children, tooltipText, headerAccessory, showChevron = true, level = 3, className, ...props },
    ref
  ) => {
    const { slots, classNames } = useAccordionContext();
    const labelRef = useRef<HTMLSpanElement>(null);
    const textOverflowing = useElementOverflowing(labelRef);

    const label = (
      <span ref={labelRef} className={cn(slots.title(), classNames?.title)}>
        {children}
      </span>
    );

    // Show overflow tooltip only if text overflows and no explicit tooltip is provided
    const title =
      !tooltipText && textOverflowing ? (
        <Tooltip
          content={<span className="text-sm font-normal leading-normal">{children}</span>}
          size="sm"
        >
          {label}
        </Tooltip>
      ) : (
        label
      );

    return (
      <Heading level={level} className={cn(slots.heading(), classNames?.heading)}>
        <Button
          ref={ref}
          slot="trigger"
          {...props}
          className={cn(slots.header(), classNames?.header, className)}
          style={{ cursor: "pointer" }}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {title}
            {tooltipText && (
              <Tooltip content={tooltipText} size="sm">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="size-4 shrink-0 text-tertiary"
                  aria-hidden
                />
              </Tooltip>
            )}
            {headerAccessory}
          </div>
          {showChevron && (
            <FontAwesomeIcon
              icon={faChevronDown}
              className={cn(slots.chevron(), classNames?.chevron)}
              aria-hidden
            />
          )}
        </Button>
      </Heading>
    );
  }
);
AccordionHeader.displayName = "AccordionHeader";
